import { noop } from "lodash";
import { useMemo, useState } from "react";

import { GroupFilter } from "src/api/endpoints";
import { useFlows } from "src/api/queries";
import { DecisionEnvironment } from "src/api/types";
import {
  FlowAccordion,
  TopLevelFilters,
} from "src/decisionsOverview/DecisionsOverview";
import { useDecisionsOverview } from "src/decisionsOverview/queries";
import {
  ManualReviewFilter,
  ManualReviewFilters,
} from "src/entities/entityView/DecisionsTab/ManualReviewStatusFilter";
import { useEnvironment } from "src/eventsCatalogue/useEnvironment";
import { ExtendedHistoricalDecisionV3 } from "src/flow/decisionHistory/Columns";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { useCurrentUserId } from "src/store/AuthStore";
import { isFlowGroup, isFlowGroupForCount } from "src/utils/predicates";
import { useParamsDecode } from "src/utils/useParamsDecode";

type Props = {
  selectedDecision: ExtendedHistoricalDecisionV3 | null;
  onSelectDecision: (decision: ExtendedHistoricalDecisionV3 | null) => void;
};

export const DecisionsTabContent: React.FC<Props> = ({
  selectedDecision,
  onSelectDecision,
}) => {
  const [manualReviewFilter, setManualReviewFilter] =
    useState<ManualReviewFilters>(ManualReviewFilters.ALL);
  const userId = useCurrentUserId();
  const { id, schema } = useParamsDecode<{ id: string; schema: string }>();
  const { workspace } = useWorkspaceContext();
  const [groupFilter] = useState<GroupFilter>({});
  const [environment] = useEnvironment() as unknown as [DecisionEnvironment];
  const [topLevelFilters] = useState<TopLevelFilters>({
    decisionEnv: environment,
    decisionId: null,
    entityId: id,
    timeWindow: undefined,
  });

  const decisionsOverviewQuery = useDecisionsOverview(workspace.base_url, {
    environments: environment,
    groupby: "flow.id",
    aggregate: "count",
    related_entity_id: id,
    related_entity_schema: schema,
    parent_decision_id: undefined,
    timeWindow: undefined,
  });

  const flowsQuery = useFlows({ workspaceId: workspace.id });

  const decisionSummary = decisionsOverviewQuery.data ?? { groups: [] };
  const decisionsOverviewQueryForCount = useDecisionsOverview(
    workspace.base_url,
    {
      environments: environment,
      groupby: "flow.id",
      aggregate: "count",
      related_entity_id: id,
      related_entity_schema: schema,
      case_assignees:
        manualReviewFilter === ManualReviewFilters.ASSIGNED_TO_ME
          ? userId
          : undefined,
      case_status:
        manualReviewFilter === ManualReviewFilters.NEEDS_REVIEW
          ? "needs_review"
          : undefined,
      timeWindow: undefined,
      groupfilter: groupFilter,
    },
  );

  const flowGroups = useMemo(
    () =>
      decisionSummary.groups
        .map((group) => ({
          flow: (flowsQuery.data ?? []).find(
            (flow) => flow.id === group.flow?.id,
          ),
        }))
        .filter(isFlowGroup)
        .sort((a, b) => a.flow?.name.localeCompare(b.flow?.name)) ?? [],
    [decisionSummary.groups, flowsQuery.data],
  );

  const flowGroupsForCount = useMemo(() => {
    return decisionsOverviewQueryForCount.data?.groups
      .map((group) => ({
        flowId: group.flow?.id,
        count: group.count,
      }))
      .filter(isFlowGroupForCount);
  }, [decisionsOverviewQueryForCount.data]);

  return (
    <div className="flex flex-col gap-y-3 overflow-hidden p-5">
      <ManualReviewFilter
        value={manualReviewFilter}
        onChange={setManualReviewFilter}
      />
      <FlowAccordion
        flowGroups={flowGroups}
        flowGroupsForCount={flowGroupsForCount}
        groupFilter={groupFilter}
        manualReviewFilter={manualReviewFilter}
        rowBehavior="expand"
        selectedDecision={selectedDecision}
        singleDecision={null}
        topLevelFilters={topLevelFilters}
        includeRelatedEntityDecisions
        onGroupFilterChange={noop}
        onSelectDecision={onSelectDecision}
      />
    </div>
  );
};
